import { useEffect, useMemo, useRef } from "react";
import { DandelionSeed } from "@/components/DandelionSeed";

/**
 * Drifting dandelion seeds across the entire viewport.
 * - Most seeds float gently with CSS keyframes
 * - One "scroll seed" follows the user's scroll position so it really
 *   appears to fly down the page as they scroll
 */
export default function PageSeeds({ count = 14, scrollSeeds = 2 }) {
  const containerRef = useRef(null);
  const scrollRefs = useRef([]);

  // Pre-compute drifting seeds (stable across renders)
  const seeds = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const startTop = Math.random() * 90 + 5;        // %
        const startLeft = Math.random() * 95 + 2;       // %
        const tx = (Math.random() - 0.4) * 280;          // px drift
        const ty = (Math.random() - 0.7) * 240;          // mostly up
        const rot = (Math.random() - 0.5) * 80;
        const dur = 22 + Math.random() * 22;             // 22–44s
        const delay = -Math.random() * 30;               // negative -> start mid-cycle
        const size = 14 + Math.random() * 18;
        const opacity = 0.35 + Math.random() * 0.35;
        return { i, startTop, startLeft, tx, ty, rot, dur, delay, size, opacity };
      }),
    [count]
  );

  // Pre-compute scroll-linked seeds
  const sSeeds = useMemo(
    () =>
      Array.from({ length: scrollSeeds }).map((_, i) => ({
        i,
        left: 8 + i * 60 + Math.random() * 14,            // %
        size: 22 + Math.random() * 10,
        speed: 0.55 + Math.random() * 0.35,               // parallax speed factor
        drift: (Math.random() - 0.5) * 250,               // horizontal drift
        rot: (Math.random() - 0.5) * 40,
      })),
    [scrollSeeds]
  );

  // Update scroll-linked seeds on scroll
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const max = Math.max(
          document.documentElement.scrollHeight - window.innerHeight,
          1
        );
        const progress = Math.min(1, y / max);
        scrollRefs.current.forEach((el, idx) => {
          if (!el) return;
          const seed = sSeeds[idx];
          const ty = y * seed.speed;
          const tx = Math.sin(progress * Math.PI * 2 + idx) * seed.drift;
          const rot = seed.rot + progress * 90;
          el.style.transform = `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg)`;
          el.style.opacity = 0.35 + progress * 0.35;
        });
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [sSeeds]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none z-[5] overflow-hidden"
    >
      {seeds.map((s) => (
        <span
          key={s.i}
          className="page-seed"
          style={{
            position: "absolute",
            top: `${s.startTop}%`,
            left: `${s.startLeft}%`,
            color: "rgba(120, 110, 95, 0.75)",
            opacity: s.opacity,
            "--ps-tx": `${s.tx}px`,
            "--ps-ty": `${s.ty}px`,
            "--ps-rot": `${s.rot}deg`,
            "--ps-dur": `${s.dur}s`,
            "--ps-delay": `${s.delay}s`,
          }}
        >
          <DandelionSeed size={s.size} />
        </span>
      ))}

      {sSeeds.map((s, idx) => (
        <span
          key={`scroll-${s.i}`}
          ref={(el) => (scrollRefs.current[idx] = el)}
          style={{
            position: "absolute",
            top: "-40px",
            left: `${s.left}%`,
            color: "rgba(120, 110, 95, 0.65)",
            willChange: "transform, opacity",
            transition: "opacity 0.4s linear",
          }}
        >
          <DandelionSeed size={s.size} />
        </span>
      ))}
    </div>
  );
}
